import { ScatterPlotMetadata, CellInfoDimension } from 'redux/slices/types'

export interface GridSection {
  left: number
  right: number
  top: number
  bottom: number
  centerX: number
  centerY: number
}

export interface GridTilePosition {
  id: string
  dimensionIndex: number
  valueIndex: number
  section: GridSection
  label: string
}

/**
 * Calculate grid sections for multi-plot layout
 * Simplified version for minimal implementation
 */
export const calculateGridSections = (
  scatterPlotMetaData: ScatterPlotMetadata,
  visibleSelectedComparisonDimensions: CellInfoDimension[]
): GridTilePosition[] => {
  if (!visibleSelectedComparisonDimensions?.length) return []

  const { minX, minY, maxX, maxY } = scatterPlotMetaData
  const width = maxX - minX

  // Match the offset calculation from getScatterPlotDataFromCellInfo.ts
  const xOffset = width * 1.5

  const positions: GridTilePosition[] = []

  // Handle first dimension (columns) - match actual subplot positioning
  const columnDimension = visibleSelectedComparisonDimensions[0]

  columnDimension.values.forEach((value, i) => {
    // Each subplot has the same width as the original plot
    // They are offset by xOffset = width * 1.5
    const leftBoundary = minX + (xOffset * i)
    const rightBoundary = leftBoundary + width

    // Full height for now (single row)
    const topBoundary = maxY
    const bottomBoundary = minY

    // Center coordinates
    const centerX = leftBoundary + (width / 2)
    const centerY = (maxY + minY) / 2

    // Use value as ID for stability across reorders
    const stableId = `tile-0-${value.replace(/\s+/g, '-')}`

    positions.push({
      id: stableId,
      dimensionIndex: 0,
      valueIndex: i,
      section: {
        left: leftBoundary,
        right: rightBoundary,
        top: topBoundary,
        bottom: bottomBoundary,
        centerX,
        centerY,
      },
      label: value,
    })
  })

  return positions
}

/**
 * Convert plot coordinates to viewport percentages for overlay positioning
 * Uses actual plot range (including padding) for accurate positioning
 */
export const convertToViewportPercentages = (
  sections: GridTilePosition[],
  _scatterPlotMetaData: ScatterPlotMetadata,
  actualRangeX: [number, number]
): Array<{
  id: string
  dimensionIndex: number
  valueIndex: number
  left: string
  top: string
  width: string
  height: string
  label: string
}> => {
  if (!sections.length) return []

  // Use the actual plot range which includes all subplots + padding
  const [plotMinX, plotMaxX] = actualRangeX
  const totalPlotWidth = plotMaxX - plotMinX

  return sections.map((section) => {
    const sectionLeft = section.section.left
    const sectionRight = section.section.right
    const sectionWidth = sectionRight - sectionLeft

    // Convert to percentages relative to actual plot range
    const leftPercent = ((sectionLeft - plotMinX) / totalPlotWidth) * 100
    const widthPercent = (sectionWidth / totalPlotWidth) * 100

    return {
      id: section.id,
      dimensionIndex: section.dimensionIndex,
      valueIndex: section.valueIndex,
      left: `${Math.max(0, leftPercent)}%`,
      top: '0%', // Full height - start at top
      width: `${Math.max(0, widthPercent)}%`,
      height: '100%', // Full height - span entire plot
      label: section.label,
    }
  })
}
